import { useCallback, useEffect, useMemo, useState } from "react"
import {
  useGetPriceFormDataQuery,
  useLazyGetPriceResortsQuery,
  useSavePriceMutation,
  useDeletePricePhotoMutation,
} from "../../../app/services/transfer/prices"

const SCALAR_FIELDS = [
  "name",
  "state_id",
  "company_id",
  "terminal_id",
  "transport_type_id",
  "from_date",
  "to_date",
  "transfer_type",
  "valute_id",
  "adult_sum",
  "adult_sum_netto",
  "child_sum",
  "child_sum_netto",
  "infant_sum",
  "infant_sum_netto",
  "adult_sum_transfer",
  "adult_sum_transfer_netto",
  "child_sum_transfer",
  "child_sum_transfer_netto",
  "infant_sum_transfer",
  "infant_sum_transfer_netto",
  "child_age_max",
  "infant_age_max",
  "passenger_from",
  "passenger_to",
  "non_refundable_days",
  "on_request_days",
  "important_info",
  "direction",
]

// Общие поля трассы (режим route) — совпадает с ROUTE_SHARED_ATTRS на бэкенде
const ROUTE_FIELDS = [
  "name",
  "direction",
  "state_id",
  "company_id",
  "terminal_id",
  "from_date",
  "to_date",
  "important_info",
  "non_refundable_days",
  "on_request_days",
]

// Что можно менять в режиме route; всё остальное — только просмотр
const ROUTE_EDITABLE = new Set([...ROUTE_FIELDS, "allow_export_api", "region_ids", "company_ids"])

export const SINGLE_PRICE_TYPES = ["rent", "private"]

const ZEROED_FOR_SINGLE = [
  "child_sum",
  "child_sum_netto",
  "infant_sum",
  "infant_sum_netto",
  "child_sum_transfer",
  "child_sum_transfer_netto",
  "infant_sum_transfer",
  "infant_sum_transfer_netto",
]

const REQUIRED = [
  "name",
  "state_id",
  "company_id",
  "terminal_id",
  "transport_type_id",
  "valute_id",
  "from_date",
  "to_date",
]

const ROUTE_REQUIRED = ["name", "state_id", "company_id", "terminal_id", "from_date", "to_date"]

export const usePriceForm = ({ urls, priceId, routeId = null, prefill = {} }) => {
  const { data, isLoading, isError } = useGetPriceFormDataQuery({
    id: priceId,
    route_id: routeId,
    ...prefill,
  })
  const [fetchResorts, { isFetching: isLoadingResorts }] = useLazyGetPriceResortsQuery()
  const [savePrice, { isLoading: isSaving }] = useSavePriceMutation()
  const [deletePhoto] = useDeletePricePhotoMutation()

  const [form, setForm] = useState(null)
  const [resorts, setResorts] = useState([])
  const [existingPhotos, setExistingPhotos] = useState([])
  const [newFiles, setNewFiles] = useState([])
  const [deletingPhotoId, setDeletingPhotoId] = useState(null)
  const [errors, setErrors] = useState({})
  const [serverErrors, setServerErrors] = useState([])

  const mode = data?.mode ?? "new"
  const isRouteMode = mode === "route"
  const isAddMode = mode === "add"
  const baseId = prefill.from_id

  const lockedSet = useMemo(() => new Set(data?.locked_fields ?? []), [data])
  // route: заблокировано всё, кроме общих полей; add/edit: список с бэкенда
  const isLocked = useCallback(
    (key) => (isRouteMode ? !ROUTE_EDITABLE.has(key) : lockedSet.has(key)),
    [isRouteMode, lockedSet]
  )
  // Если у колонки нет типа (id=0) — блокировать нечего, иначе форму не сохранить
  const transportLocked =
    isRouteMode || Boolean(data?.lock_transport_type && form?.transport_type_id)

  useEffect(() => {
    if (!data) return
    setForm(data.price)
    setResorts(data.options.resorts)
    setExistingPhotos(data.price.photos ?? [])
  }, [data])

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev))
  }, [])

  const changeState = useCallback(
    async (stateId) => {
      setForm((prev) => ({ ...prev, state_id: stateId, region_ids: [] }))
      setErrors((prev) => ({ ...prev, state_id: undefined }))
      if (!stateId) return setResorts([])
      try {
        const res = await fetchResorts({ state_id: stateId }).unwrap()
        setResorts(res.resorts)
      } catch (_) {
        setResorts([])
      }
    },
    [fetchResorts]
  )

  const selectAllResorts = useCallback(
    () =>
      setField(
        "region_ids",
        resorts.map((r) => r.id)
      ),
    [resorts, setField]
  )
  const selectAllHotels = useCallback(
    () =>
      setField(
        "company_ids",
        (data?.options.hotel_companies ?? []).map((c) => c.id)
      ),
    [data, setField]
  )

  const addFiles = useCallback((files) => setNewFiles((prev) => [...prev, ...files]), [])
  const removeNewFile = useCallback(
    (i) => setNewFiles((prev) => prev.filter((_, idx) => idx !== i)),
    []
  )

  const removeExistingPhoto = useCallback(
    async (id) => {
      setDeletingPhotoId(id)
      try {
        await deletePhoto({ id }).unwrap()
        setExistingPhotos((prev) => prev.filter((p) => p.id !== id))
      } finally {
        setDeletingPhotoId(null)
      }
    },
    [deletePhoto]
  )

  const isSinglePrice = useMemo(
    () => SINGLE_PRICE_TYPES.includes(form?.transfer_type),
    [form?.transfer_type]
  )

  const validate = () => {
    const next = {}
    ;(isRouteMode ? ROUTE_REQUIRED : REQUIRED).forEach((k) => {
      if (!form[k]) next[k] = true
    })
    if (form.from_date && form.to_date && form.from_date > form.to_date) next.to_date = true
    if (!isRouteMode && Number(form.passenger_from) > Number(form.passenger_to)) {
      next.passenger_to = true
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const appendLinks = (fd) => {
    // "" гарантирует, что ключ попадёт в params даже при пустом выборе
    fd.append("regions[ids][]", "")
    form.region_ids.forEach((id) => fd.append("regions[ids][]", id))
    fd.append("companies[ids][]", "")
    form.company_ids.forEach((id) => fd.append("companies[ids][]", id))
  }

  const buildFormData = () => {
    const fd = new FormData()

    if (isRouteMode) {
      ROUTE_FIELDS.forEach((k) => fd.append(`price[${k}]`, form[k] ?? ""))
      fd.append("price[allow_export_api]", form.allow_export_api ? "1" : "0")
      appendLinks(fd)
      return fd
    }

    const values = { ...form }
    if (isSinglePrice) ZEROED_FOR_SINGLE.forEach((k) => (values[k] = 0))

    SCALAR_FIELDS.forEach((k) => fd.append(`price[${k}]`, values[k] ?? ""))
    fd.append("price[allow_export_api]", form.allow_export_api ? "1" : "0")
    if (isAddMode && baseId) fd.append("base_id", baseId)

    appendLinks(fd)
    newFiles.forEach((f) => fd.append("price_photos[]", f))
    return fd
  }

  const submit = useCallback(async () => {
    setServerErrors([])
    if (!validate()) return
    try {
      const res = await savePrice({
        id: isRouteMode ? routeId : priceId,
        body: buildFormData(),
        route: isRouteMode,
      }).unwrap()
      const target = res.redirect_url ?? urls.prices
      window.Turbolinks?.visit ? window.Turbolinks.visit(target) : window.location.assign(target)
    } catch (e) {
      setServerErrors(e?.data?.errors ?? ["Ошибка сохранения"])
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, newFiles, priceId, routeId, urls.prices, isSinglePrice, isRouteMode, isAddMode, baseId])

  return {
    form,
    setField,
    options: data?.options,
    settings: data?.settings ?? {},
    mode,
    isRouteMode,
    isAddMode,
    isLocked,
    transportLocked,
    baseName: data?.base_name,
    routePricesCount: data?.route_prices_count,
    resorts,
    isLoadingResorts,
    isLoading,
    isError,
    isSaving,
    isSinglePrice,
    errors,
    serverErrors,
    changeState,
    selectAllResorts,
    selectAllHotels,
    existingPhotos,
    newFiles,
    addFiles,
    removeNewFile,
    removeExistingPhoto,
    deletingPhotoId,
    submit,
  }
}
